import type { ReactNode } from "react"
import { Link, type To } from "react-router"
import { cn } from "@/primitives/utils"
import { rowHighlight } from "./ticket-row.tsx"

/**
 * A list row's frame: 48px and 16px in on the desk; on `compact` a stacked row, at least 56px, as
 * tall as its lines and ruled off from the next. A DataTable's stacked rows, a `ListRow` and their
 * skeletons wear it, so every list on a phone keeps one rhythm. Internal to the design system.
 */
export const listRowFrame =
  "flex h-12 items-center gap-3 px-4 compact:h-auto compact:min-h-14 compact:scroll-mt-32 compact:border-b compact:border-border-subtle compact:py-2"

/**
 * One row of a list of links that isn't a table, as Groups' list is: its `lead` (a GroupDot), its
 * name over its `secondary` line (how many are in it), and what `trailing` ends it with (who). On
 * the desk it is 48px, the name cut short on its one line; on `compact` it is a stacked row, as a
 * DataTable's are, the name wrapping to two lines. The row is the link, a `data-row` that
 * `useRowNavigation` moves through, highlighted as any such row is (`rowHighlight`); `current`
 * fills it as the one chosen, keeping the fill under the pointer and the highlight, so the bar alone
 * says the keys are on it.
 */
export function ListRow({
  to,
  replace,
  current = false,
  lead,
  name,
  secondary,
  trailing,
}: {
  to: To
  /** Whether choosing it replaces the history entry rather than adding one. */
  replace?: boolean
  current?: boolean
  lead?: ReactNode
  name: ReactNode
  secondary?: ReactNode
  trailing?: ReactNode
}) {
  return (
    <Link
      to={to}
      replace={replace ?? false}
      data-row
      aria-current={current ? "page" : undefined}
      className={cn(
        listRowFrame,
        "transition-colors duration-100 ease-snappy",
        rowHighlight,
        current &&
          "bg-selected hover:bg-selected focus-visible:bg-selected data-highlighted:bg-selected",
      )}
    >
      {lead}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ui font-medium compact:line-clamp-2 compact:whitespace-normal compact:wrap-break-word">
          {name}
        </span>
        {secondary !== undefined && (
          <span className="block text-xs text-subtle-foreground tabular-nums">{secondary}</span>
        )}
      </span>
      {trailing}
    </Link>
  )
}
